import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { STAFF_ONLY_ROLES } from "@/auth/computeProtectedRedirect";
import { NotesProvider, HeaderMessagesButton, useNotes } from "@/components/notes/NotesHub";
import { CommunityTab } from "@/components/community/CommunityTab";
import { CollaborationDetailModal } from "@/components/community/CollaborationDetailModal";

// Wave 4 (2026-09) Fix 3 — a "new message" email's CTA appends `?conversationId=`. This tiny
// inner component (mounted INSIDE NotesProvider, so it can reach `useNotes()`) reuses the EXACT
// SAME `openThread()` the notifications tab already calls — no new deep-link mechanism.
function AutoOpenThread() {
  const [params] = useSearchParams();
  const { openThread } = useNotes();
  useEffect(() => {
    const conversationId = params.get("conversationId");
    if (conversationId) openThread(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Collaboration UX Wave 3 (2026-09) — a stable, shareable deep link for one Collaboration.
// Mirrors /build/member/:id's route shape/gating (`communityMember` — works for BOTH staff and
// freelancer sessions, since "the Floor" has two different homes: /community for staff,
// /build/me's client-side tab for freelancers). Deliberately reuses `CollaborationDetailModal`
// and `CommunityTab` VERBATIM, mounted fresh on this route — no changes to either component's
// own state management, no changes to how a card opens the modal from inside the normal feed.
// "Not found" (deleted/private/invalid id) is already handled by CollaborationDetailModal's own
// fetch-failure branch — reused as-is, nothing new needed here for that case.
export default function CollaborationDeepLink() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const backToFloor = user && STAFF_ONLY_ROLES.includes(user.role) ? "/community" : "/build/me?tab=community";

  return (
    <NotesProvider>
      <AutoOpenThread />
      <div className="mx-auto max-w-3xl px-4 sm:px-8 py-6 sm:py-10" data-testid="collaboration-deep-link-page">
        <div className="flex justify-end mb-2"><div className="bg-ink rounded-full inline-flex"><HeaderMessagesButton /></div></div>
        <CommunityTab />
      </div>
      <CollaborationDetailModal
        opportunityId={id}
        viewerIsFreelancer={user?.role === "freelancer"}
        onClose={() => navigate(backToFloor, { replace: true })}
        onChanged={() => {}}
      />
    </NotesProvider>
  );
}
